import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  MoreVertical,
  Eye,
  Ban,
  CheckCircle,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [page, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search })
      });
      
      const response = await api.get(`/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get(`/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const initiateStatusChange = (userId, newStatus) => {
    setPendingAction({ userId, status: newStatus });
    setShowReasonModal(true);
    setShowActionMenu(null);
  };

  const executeStatusChange = async () => {
    if (!pendingAction) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(
        `/admin/users/${pendingAction.userId}/status`,
        { status: pendingAction.status, reason: actionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`User ${pendingAction.status} successfully`);
      setShowReasonModal(false);
      setActionReason('');
      setPendingAction(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      await api.delete(`/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
    setShowActionMenu(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400',
      suspended: 'bg-yellow-500/20 text-yellow-400',
      banned: 'bg-red-500/20 text-red-400',
      deleted: 'bg-gray-500/20 text-gray-400'
    };
    return styles[status] || styles.active;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">User Management</h1>
        <span className="text-gray-400">{total} total users</span>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or phone..."
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </form>
        <div className="flex items-center space-x-2">
          <Filter className="text-gray-400 w-5 h-5" />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-gray-800 border border-gray-700 rounded-lg text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-750">
              <tr className="text-left text-gray-400 text-sm">
                <th className="px-6 py-4">User</th>
                <th className="px-6 py-4">Phone</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Verified</th>
                <th className="px-6 py-4">Joined</th>
                <th className="px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-t border-gray-700 hover:bg-gray-750">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                          {user.photos?.[0] ? (
                            <img src={user.photos[0]} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-white font-semibold">
                              {(user.name || 'U')[0].toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name || 'No Name'}</p>
                          <p className="text-gray-400 text-sm">{user.gender || 'N/A'}, {user.age || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{user.phone_number}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(user.status)}`}>
                        {user.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.verified ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <X className="w-5 h-5 text-gray-500" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative">
                        <button
                          onClick={() => setShowActionMenu(showActionMenu === user.id ? null : user.id)}
                          className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>
                        {showActionMenu === user.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-gray-700 rounded-lg shadow-lg z-10 py-1">
                            <button
                              onClick={() => { fetchUserDetails(user.id); setShowActionMenu(null); }}
                              className="w-full px-4 py-2 text-left text-gray-300 hover:bg-gray-600 flex items-center space-x-2"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Details</span>
                            </button>
                            {user.status !== 'suspended' && (
                              <button
                                onClick={() => initiateStatusChange(user.id, 'suspended')}
                                className="w-full px-4 py-2 text-left text-yellow-400 hover:bg-gray-600 flex items-center space-x-2"
                              >
                                <Ban className="w-4 h-4" />
                                <span>Suspend</span>
                              </button>
                            )}
                            {user.status !== 'active' && (
                              <button
                                onClick={() => initiateStatusChange(user.id, 'active')}
                                className="w-full px-4 py-2 text-left text-green-400 hover:bg-gray-600 flex items-center space-x-2"
                              >
                                <CheckCircle className="w-4 h-4" />
                                <span>Activate</span>
                              </button>
                            )}
                            {user.status !== 'banned' && (
                              <button
                                onClick={() => initiateStatusChange(user.id, 'banned')}
                                className="w-full px-4 py-2 text-left text-red-400 hover:bg-gray-600 flex items-center space-x-2"
                              >
                                <Ban className="w-4 h-4" />
                                <span>Ban</span>
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="w-full px-4 py-2 text-left text-red-400 hover:bg-gray-600 flex items-center space-x-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
          <p className="text-gray-400 text-sm">
            Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total}
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-white px-4">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">User Details</h2>
              <button onClick={() => setShowUserModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="flex items-start space-x-4">
                <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center">
                  {selectedUser.user.photos?.[0] ? (
                    <img src={selectedUser.user.photos[0]} alt="" className="w-20 h-20 rounded-full object-cover" />
                  ) : (
                    <span className="text-white text-2xl font-bold">
                      {(selectedUser.user.name || 'U')[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-xl text-white font-semibold">{selectedUser.user.name || 'No Name'}</h3>
                  <p className="text-gray-400">{selectedUser.user.phone_number}</p>
                  <p className="text-gray-400">{selectedUser.user.gender}, {selectedUser.user.age} years old</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs mt-2 ${getStatusBadge(selectedUser.user.status)}`}>
                    {selectedUser.user.status || 'active'}
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{selectedUser.stats.matches}</p>
                  <p className="text-gray-400 text-sm">Matches</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{selectedUser.stats.messages_sent}</p>
                  <p className="text-gray-400 text-sm">Messages</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{selectedUser.stats.reports_received}</p>
                  <p className="text-gray-400 text-sm">Reports</p>
                </div>
              </div>

              {/* Bio */}
              {selectedUser.user.bio && (
                <div>
                  <h4 className="text-white font-medium mb-2">Bio</h4>
                  <p className="text-gray-400">{selectedUser.user.bio}</p>
                </div>
              )}

              {/* Subscription */}
              <div>
                <h4 className="text-white font-medium mb-2">Subscription</h4>
                {selectedUser.subscription ? (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-white capitalize">{selectedUser.subscription.tier} Plan</p>
                    <p className="text-gray-400 text-sm">Status: {selectedUser.subscription.status}</p>
                  </div>
                ) : (
                  <p className="text-gray-500">No active subscription</p>
                )}
              </div>

              {/* Reports */}
              {selectedUser.reports.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-2">Reports ({selectedUser.reports.length})</h4>
                  <div className="space-y-2">
                    {selectedUser.reports.map((report) => (
                      <div key={report.id} className="bg-gray-700 rounded-lg p-3">
                        <p className="text-white text-sm">{report.reason}</p>
                        <p className="text-gray-400 text-xs">{report.details}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-white mb-4">
              {pendingAction?.status === 'suspended' ? 'Suspend' : 
               pendingAction?.status === 'banned' ? 'Ban' : 'Activate'} User
            </h2>
            <textarea
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              placeholder="Enter reason (optional)..."
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4"
              rows={3}
            />
            <div className="flex space-x-3">
              <button
                onClick={() => { setShowReasonModal(false); setPendingAction(null); setActionReason(''); }}
                className="flex-1 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={executeStatusChange}
                className={`flex-1 py-2 rounded-lg text-white ${
                  pendingAction?.status === 'active' ? 'bg-green-600 hover:bg-green-700' :
                  pendingAction?.status === 'banned' ? 'bg-red-600 hover:bg-red-700' :
                  'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
